import { Heart } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Heart toggle for events and horses. Guests are sent to sign in.
 * `initial` seeds the state (from /favorites/ids); `onChange` notifies parents.
 */
export const FavoriteButton = ({ entityType, entityId, initial = false, className = "", onChange }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [favorited, setFavorited] = useState(initial);
  const [busy, setBusy] = useState(false);

  // Sync when the parent's favorites load asynchronously
  useEffect(() => setFavorited(initial), [initial]);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      navigate("/signin", { state: { from: location.pathname } });
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/favorites/toggle", { entity_type: entityType, entity_id: entityId });
      setFavorited(data.favorited);
      toast(t(data.favorited ? "acct.favAdded" : "acct.favRemoved"));
      onChange?.(data.favorited);
    } catch {
      toast(t("auth.genericError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={toggle}
      data-testid={`favorite-${entityType}-${entityId}`}
      aria-label={t(favorited ? "acct.favRemoved" : "acct.favAdded")}
      aria-pressed={favorited}
      className={`inline-flex items-center justify-center h-9 w-9 rounded-full backdrop-blur-xl bg-white/85 border border-black/5 hover:scale-110 transition-transform duration-200 ${className}`}
    >
      <Heart
        className={`w-4 h-4 transition-colors duration-200 ${favorited ? "fill-[#1D4D3B] text-[#1D4D3B]" : "text-[#0A0A0A]/50"}`}
        aria-hidden="true"
      />
    </button>
  );
};
